import React, { useState, useEffect } from 'react';
import './InvestmentOpportunityManagement.css';
import {
  InvestmentOpportunity,
  InvestmentOpportunityFormData,
  InvestmentOpportunityFilters,
  InvestmentStatus,
  RiskLevel,
  STATUS_LABELS,
  RISK_LEVEL_LABELS,
  INVESTMENT_CATEGORIES
} from '../types/InvestmentOpportunity';
import { investmentOpportunityService } from '../services/investmentOpportunityService';
// Import InvestmentOpportunityForm lazily to avoid potential issues
const InvestmentOpportunityForm = React.lazy(() => import('./InvestmentOpportunityForm'));

interface InvestmentOpportunityManagementState {
  opportunities: InvestmentOpportunity[];
  loading: boolean;
  error: string | null;
  showForm: boolean;
  editingOpportunity: InvestmentOpportunity | null;
  filters: InvestmentOpportunityFilters;
  statistics: {
    total: number;
    available: number;
    active: number;
    completed: number;
    suspended: number;
    totalInvestmentAmount: number;
  } | null;
}

const InvestmentOpportunityManagement: React.FC = () => {
  const [state, setState] = useState<InvestmentOpportunityManagementState>({
    opportunities: [],
    loading: false,
    error: null,
    showForm: false,
    editingOpportunity: null,
    filters: {},
    statistics: null
  });

  // Load data on component mount
  useEffect(() => {
    loadOpportunities();
    loadStatistics();
  }, [state.filters]);

  const loadOpportunities = async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const opportunities = await investmentOpportunityService.getAll(state.filters);
      setState(prev => ({ ...prev, opportunities, loading: false }));
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        loading: false, 
        error: 'Gagal memuat data peluang investasi' 
      }));
    }
  };

  const loadStatistics = async () => {
    try {
      const statistics = await investmentOpportunityService.getStatistics();
      setState(prev => ({ ...prev, statistics }));
    } catch (error) {
      console.error('Failed to load statistics:', error);
    }
  };

  const handleAddNew = () => {
    setState(prev => ({ 
      ...prev, 
      showForm: true, 
      editingOpportunity: null 
    }));
  };

  const handleEdit = (opportunity: InvestmentOpportunity) => {
    setState(prev => ({ 
      ...prev, 
      showForm: true, 
      editingOpportunity: opportunity 
    }));
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus peluang investasi ini?')) {
      return;
    }

    try {
      await investmentOpportunityService.delete(id);
      await loadOpportunities();
      await loadStatistics();
      alert('Peluang investasi berhasil dihapus');
    } catch (error) {
      alert('Gagal menghapus peluang investasi');
    }
  };

  const handleToggleActive = async (id: string) => {
    try {
      await investmentOpportunityService.toggleActive(id);
      await loadOpportunities();
      alert('Status berhasil diubah');
    } catch (error) {
      alert('Gagal mengubah status');
    }
  };

  const handleFormSubmit = async (data: InvestmentOpportunityFormData) => {
    setState(prev => ({ ...prev, loading: true }));
    
    try {
      if (state.editingOpportunity) {
        await investmentOpportunityService.update(state.editingOpportunity.id, data);
        alert('Peluang investasi berhasil diupdate');
      } else {
        await investmentOpportunityService.create(data);
        alert('Peluang investasi berhasil ditambahkan');
      }
      
      setState(prev => ({ 
        ...prev, 
        showForm: false, 
        editingOpportunity: null, 
        loading: false 
      }));
      
      await loadOpportunities();
      await loadStatistics();
    } catch (error) {
      setState(prev => ({ ...prev, loading: false }));
      alert('Gagal menyimpan peluang investasi');
    }
  };

  const handleFormCancel = () => {
    setState(prev => ({ 
      ...prev, 
      showForm: false, 
      editingOpportunity: null 
    }));
  };

  const handleFilterChange = (newFilters: Partial<InvestmentOpportunityFilters>) => {
    setState(prev => ({ 
      ...prev, 
      filters: { ...prev.filters, ...newFilters } 
    }));
  };

  const handleClearFilters = () => {
    setState(prev => ({ ...prev, filters: {} }));
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value);
  };

  const getStatusBadgeClass = (status: InvestmentStatus) => {
    switch (status) {
      case InvestmentStatus.AVAILABLE:
        return 'status-available';
      case InvestmentStatus.ACTIVE:
        return 'status-active';
      case InvestmentStatus.COMPLETED:
        return 'status-completed';
      case InvestmentStatus.SUSPENDED:
        return 'status-suspended';
      default:
        return '';
    }
  };

  const getRiskBadgeClass = (risk: RiskLevel) => {
    switch (risk) {
      case RiskLevel.LOW:
        return 'risk-low';
      case RiskLevel.MEDIUM:
        return 'risk-medium';
      case RiskLevel.HIGH:
        return 'risk-high';
      default:
        return '';
    }
  };

  if (state.showForm) {
    return (
      <React.Suspense fallback={<div className="loading-state"><div className="spinner"></div><p>Loading form...</p></div>}>
        <InvestmentOpportunityForm
          opportunity={state.editingOpportunity ? state.editingOpportunity : undefined}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          isLoading={state.loading}
        />
      </React.Suspense>
    );
  }

  return (
    <div className="investment-opportunity-management">
      <div className="page-header">
        <div className="header-content">
          <h1>Manajemen Peluang Investasi</h1>
          <p>Kelola dan pantau semua peluang investasi di Sukoharjo</p>
        </div>
        <button className="btn-primary" onClick={handleAddNew}>
          + Tambah Peluang Investasi
        </button>
      </div>

      {/* Statistics Cards */}
      {state.statistics && (
        <div className="statistics-grid">
          <div className="stat-card">
            <div className="stat-value">{state.statistics.total}</div>
            <div className="stat-label">Total Peluang</div>
          </div>
          <div className="stat-card available">
            <div className="stat-value">{state.statistics.available}</div>
            <div className="stat-label">Tersedia</div>
          </div>
          <div className="stat-card active">
            <div className="stat-value">{state.statistics.active}</div>
            <div className="stat-label">Aktif</div>
          </div>
          <div className="stat-card completed">
            <div className="stat-value">{state.statistics.completed}</div>
            <div className="stat-label">Selesai</div>
          </div>
          <div className="stat-card total-investment">
            <div className="stat-value">
              {formatCurrency(state.statistics.totalInvestmentAmount)}
            </div>
            <div className="stat-label">Total Investasi</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-grid">
          <div className="filter-group">
            <label>Pencarian</label>
            <input
              type="text"
              placeholder="Cari berdasarkan judul, deskripsi, atau lokasi..."
              value={state.filters.search || ''}
              onChange={(e) => handleFilterChange({ search: e.target.value })}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label>Kategori</label>
            <select
              value={state.filters.category || ''}
              onChange={(e) => handleFilterChange({ 
                category: e.target.value || undefined 
              })}
              className="filter-select"
            >
              <option value="">Semua Kategori</option>
              {INVESTMENT_CATEGORIES.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Status</label>
            <select
              value={state.filters.status || ''}
              onChange={(e) => handleFilterChange({ 
                status: e.target.value ? parseInt(e.target.value) as InvestmentStatus : undefined 
              })}
              className="filter-select"
            >
              <option value="">Semua Status</option>
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Tingkat Risiko</label>
            <select
              value={state.filters.riskLevel || ''}
              onChange={(e) => handleFilterChange({ 
                riskLevel: e.target.value as RiskLevel || undefined 
              })}
              className="filter-select"
            >
              <option value="">Semua Risiko</option>
              {Object.entries(RISK_LEVEL_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-actions">
            <button 
              className="btn-secondary" 
              onClick={handleClearFilters}
            >
              Reset Filter
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {state.error && (
        <div className="error-message">
          {state.error}
        </div>
      )}

      {/* Loading State */}
      {state.loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Memuat data...</p>
        </div>
      )}

      {/* Opportunities Table */}
      {!state.loading && (
        <div className="opportunities-table-container">
          <div className="table-header">
            <h2>Daftar Peluang Investasi ({state.opportunities.length})</h2>
          </div>

          {state.opportunities.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📊</div>
              <h3>Belum ada peluang investasi</h3>
              <p>Mulai dengan menambahkan peluang investasi baru</p>
              <button className="btn-primary" onClick={handleAddNew}>
                + Tambah Peluang Investasi
              </button>
            </div>
          ) : (
            <div className="opportunities-table">
              <table>
                <thead>
                  <tr>
                    <th>Judul</th>
                    <th>Kategori</th>
                    <th>Lokasi</th>
                    <th>Jumlah Investasi</th>
                    <th>Return</th>
                    <th>Durasi</th>
                    <th>Status</th>
                    <th>Risiko</th>
                    <th>Rating</th>
                    <th>Status Aktif</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {state.opportunities.map((opportunity) => (
                    <tr key={opportunity.id}>
                      <td>
                        <div className="opportunity-title">
                          <strong>{opportunity.title}</strong>
                          <div className="opportunity-sectors">
                            {opportunity.sectors.slice(0, 2).map(sector => (
                              <span key={sector} className="sector-tag">
                                {sector}
                              </span>
                            ))}
                            {opportunity.sectors.length > 2 && (
                              <span className="sector-more">
                                +{opportunity.sectors.length - 2}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>{opportunity.category}</td>
                      <td>{opportunity.location}</td>
                      <td>
                        <div className="investment-amount">
                          {formatCurrency(opportunity.investmentAmount)}
                        </div>
                      </td>
                      <td>
                        <span className="return-rate">
                          {opportunity.expectedReturn}%
                        </span>
                      </td>
                      <td>
                        <span className="duration">
                          {opportunity.duration} bulan
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${getStatusBadgeClass(opportunity.status)}`}>
                          {STATUS_LABELS[opportunity.status]}
                        </span>
                      </td>
                      <td>
                        <span className={`risk-badge ${getRiskBadgeClass(opportunity.riskLevel)}`}>
                          {RISK_LEVEL_LABELS[opportunity.riskLevel]}
                        </span>
                      </td>
                      <td>
                        <div className="rating">
                          <span className="rating-value">{opportunity.rating}</span>
                          <span className="rating-stars">★</span>
                        </div>
                      </td>
                      <td>
                        <button
                          className={`toggle-btn ${opportunity.isActive ? 'active' : 'inactive'}`}
                          onClick={() => handleToggleActive(opportunity.id)}
                        >
                          {opportunity.isActive ? 'Aktif' : 'Nonaktif'}
                        </button>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-edit"
                            onClick={() => handleEdit(opportunity)}
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => handleDelete(opportunity.id)}
                            title="Hapus"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InvestmentOpportunityManagement;